type MailtoOptions = {
  to?: string;
  subject?: string;
  body?: string;
};

type EmailComposerPlugin = {
  open?: (options: {
    to?: string | string[];
    subject?: string;
    body?: string;
  }, callback?: () => void) => void;
  hasClient?: (app: string, callback: (available: boolean) => void) => void;
};

const getCordova = () => (window as any).cordova as {
  platformId?: string;
  plugins?: { email?: EmailComposerPlugin };
  InAppBrowser?: { open: (url: string, target: string, options?: string) => void };
} | undefined;

/**
 * Opens a mailto link on web and in Cordova.
 * Plain <a href="mailto:..."> and InAppBrowser mailto often do nothing in iOS WKWebView.
 * On iOS native builds, prefer cordova-plugin-email-composer (MFMailCompose).
 */
export function openMailtoUrl(mailtoUrl: string, options?: MailtoOptions): void {
  const cordova = getCordova();
  const emailPlugin = cordova?.plugins?.email;
  const to = options?.to;
  const subject = options?.subject ?? '';
  const body = options?.body ?? '';

  const openViaEmailComposer = () => {
    if (!emailPlugin?.open || !to) return false;
    emailPlugin.open({ to, subject, body });
    return true;
  };

  // Match DeleteAccount / MyStats: iOS Cordova uses the email-composer plugin.
  if (cordova?.platformId === 'ios' && emailPlugin?.open && to) {
    if (emailPlugin.hasClient) {
      emailPlugin.hasClient('mailto', (available) => {
        if (available) {
          emailPlugin.open?.({ to, subject, body });
          return;
        }
        // Simulator with no Mail account configured — hasClient returns false.
        window.location.href = mailtoUrl;
      });
      return;
    }
    if (openViaEmailComposer()) return;
  }

  if (openViaEmailComposer()) return;

  // InAppBrowser mailto is unreliable on iOS; still fine on Android.
  if (cordova?.platformId !== 'ios' && cordova?.InAppBrowser?.open) {
    cordova.InAppBrowser.open(mailtoUrl, '_system', 'location=yes');
    return;
  }

  if (typeof window.open === 'function') {
    const opened = window.open(mailtoUrl, '_system');
    if (opened) return;
  }

  window.location.href = mailtoUrl;
}
